import { City, Province } from "./dto/response/location-response-dto";

interface CacheData {
  provinces: Province[];
  cities: { [provinceId: number]: City[] };
  lastUpdated: number;
}

class LocationCacheService {
  private cache: CacheData = {
    provinces: [],
    cities: {},
    lastUpdated: 0
  };
  
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 horas en ms

  // Verificar si el caché es válido
  private isCacheValid(): boolean {
    return Date.now() - this.cache.lastUpdated < this.CACHE_DURATION;
  }

  // Obtener provincias del caché
  getProvinces(): Province[] | null {
    if (this.isCacheValid() && this.cache.provinces.length > 0) {
      return this.cache.provinces;
    }
    return null;
  }

  // Obtener ciudades del caché
  getCities(provinceId: number): City[] | null {
    if (this.isCacheValid() && this.cache.cities[provinceId]) {
      return this.cache.cities[provinceId];
    }
    return null;
  }

  // Guardar provincias en caché
  setProvinces(provinces: Province[]): void {
    this.cache.provinces = provinces;
    this.cache.lastUpdated = Date.now();
    
    // Guardar en localStorage para persistencia
    try {
      localStorage.setItem('locationCache', JSON.stringify(this.cache));
    } catch (error) {
      console.warn('No se pudo guardar el caché en localStorage:', error);
    }
  }

  // Guardar ciudades en caché
  setCities(provinceId: number, cities: City[]): void {
    this.cache.cities[provinceId] = cities;
    this.cache.lastUpdated = Date.now();
    
    // Guardar en localStorage para persistencia
    try {
      localStorage.setItem('locationCache', JSON.stringify(this.cache));
    } catch (error) {
      console.warn('No se pudo guardar el caché en localStorage:', error);
    }
  }

  // Cargar caché desde localStorage al inicializar
  loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('locationCache');
      if (stored) {
        const parsedCache = JSON.parse(stored);
        if (this.isCacheValidFromData(parsedCache)) {
          this.cache = parsedCache;
        }
      }
    } catch (error) {
      console.warn('Error al cargar caché desde localStorage:', error);
    }
  }

  private isCacheValidFromData(cacheData: CacheData): boolean {
    return Date.now() - cacheData.lastUpdated < this.CACHE_DURATION;
  }

  // Limpiar caché
  clearCache(): void {
    this.cache = {
      provinces: [],
      cities: {},
      lastUpdated: 0
    };
    localStorage.removeItem('locationCache');
  }
}

// Instancia singleton
export const locationCacheService = new LocationCacheService();
